import React, { useState } from 'react';
import { X, Settings, Save, RotateCcw, AlertTriangle } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { formatSettingValue, parseSettingValue, getCategoryDisplayName } from '../utils/settingsUtils';
import { SettingCategory } from '../types/settings';
import { IconCircleButton } from './IconCircleButton';

interface AdminSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userFid: string;
}

export const AdminSettingsModal: React.FC<AdminSettingsModalProps> = ({
  isOpen,
  onClose,
  userFid
}) => {
  const { settings, loading, error, getSetting, updateSetting, refreshSettings } = useSettings(userFid);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  if (!isOpen) return null;

  const startEditing = (key: string, currentValue: any, dataType: string) => {
    setEditingKey(key);
    setEditValue(formatSettingValue(currentValue, dataType));
    setSaveError(null);
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditValue('');
    setSaveError(null);
  };

  const saveEdit = async (key: string, dataType: string) => {
    setSaving(true);
    setSaveError(null);

    try {
      const parsedValue = parseSettingValue(dataType, editValue);
      const success = await updateSetting(key, parsedValue);
      
      if (success) {
        setEditingKey(null);
        setEditValue('');
      } else {
        setSaveError('Failed to save setting');
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setSaveError(null);
    await refreshSettings();
  };

      // Group settings by category
    console.log('🔍 Admin Settings Debug:', { settings, loading, error });
    
    const groupedSettings = Object.entries(settings).reduce((acc, [key, settingData]) => {
      if (typeof settingData === 'object' && settingData.category) {
        const category = settingData.category || 'other';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push({
          key,
          value: settingData.value,
          description: settingData.description,
          data_type: settingData.data_type,
          updated_at: settingData.updated_at,
        });
      }
      return acc;
    }, {} as Record<string, any[]>);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-600/50">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-600/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Settings className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Admin Settings</h2>
              <p className="text-sm text-gray-400">Manage application configuration</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title="Refresh settings"
            >
              <RotateCcw className="w-5 h-5 text-gray-400" />
            </button>
            <IconCircleButton
              onClick={onClose}
              aria-label="Close"
              size={36}
              className="bg-black/45 hover:bg-black/60 border-white/15 text-white/80 shadow-lg backdrop-blur-sm"
              icon={<X className="w-4 h-4" />}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-240px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-400">Loading settings...</span>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-red-400">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">Error loading settings</span>
              </div>
              <p className="text-sm text-red-300 mt-2">{error}</p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedSettings).map(([category, categorySettings]) => (
                <div key={category} className="space-y-4">
                  <h3 className="text-lg font-semibold text-white border-b border-gray-600/30 pb-2">
                    {getCategoryDisplayName(category)}
                  </h3>
                  
                  <div className="grid gap-4">
                    {categorySettings.map((setting) => (
                      <div
                        key={setting.key}
                        className="bg-gray-800/50 rounded-lg p-4 border border-gray-600/30"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium text-white">{setting.key}</h4>
                              <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                                {setting.data_type}
                              </span>
                            </div>
                            {setting.description && (
                              <p className="text-sm text-gray-400 mt-1">{setting.description}</p>
                            )}
                            
                            {editingKey === setting.key ? (
                              <div className="mt-3 space-y-2">
                                <textarea
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm resize-vertical min-h-[60px]"
                                  placeholder={`Enter ${setting.data_type} value...`}
                                />
                                {saveError && (
                                  <p className="text-red-400 text-xs">{saveError}</p>
                                )}
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => saveEdit(setting.key, setting.data_type)}
                                    disabled={saving}
                                    className="flex items-center space-x-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 text-white rounded-lg text-xs transition-colors"
                                  >
                                    <Save className="w-3 h-3" />
                                    <span>{saving ? 'Saving...' : 'Save'}</span>
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-xs transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-3">
                                <div className="bg-gray-700/50 rounded p-2 font-mono text-sm text-gray-200">
                                  {formatSettingValue(setting.value, setting.data_type)}
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-xs text-gray-500">
                                    Updated: {new Date(setting.updated_at).toLocaleString()}
                                  </span>
                                  <button
                                    onClick={() => startEditing(setting.key, setting.value, setting.data_type)}
                                    className="px-2 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded text-xs transition-colors"
                                  >
                                    Edit
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-600/50 bg-gray-800/30">
          <div className="flex items-center justify-between text-sm text-gray-400">
            <span>💡 Changes take effect immediately</span>
            <span>🔒 Admin access required</span>
          </div>
        </div>
      </div>
    </div>
  );
};
